import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { organizationsService, type OrgInvitation } from '@/api/services/organizations';
import { useMe } from '@/context/MeContext';
import { formatApiError } from '@/utils/formatApiError';

import { workspaceRoleLabel } from '@/utils/workspaceRoles';

type Props = {
  token: string;
  compact?: boolean;
};

function roleLabel(role: string): string {
  return workspaceRoleLabel(role);
}

export default function InvitationAcceptPanel({ token, compact }: Props) {
  const navigate = useNavigate();
  const { me, refresh } = useMe();
  const [invitation, setInvitation] = useState<OrgInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await organizationsService.getInvitation(token);
      setInvitation(data);
    } catch (err) {
      setError(formatApiError(err, 'Invitation not found or no longer valid'));
      setInvitation(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function accept() {
    setAccepting(true);
    setError('');
    try {
      await organizationsService.acceptInvitation(token);
      setAccepted(true);
      await refresh();
      if (!compact) {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err, 'Could not accept invitation'));
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return <p className="muted">Loading invitation…</p>;
  }

  if (!invitation) {
    return error ? <div className="alert alert-error">{error}</div> : null;
  }

  const emailMatches =
    !me?.email ||
    !invitation.email ||
    me.email.trim().toLowerCase() === invitation.email.trim().toLowerCase();
  const canAccept =
    invitation.status === 'pending' && emailMatches && !accepted;

  return (
    <div className={`invitation-accept-panel${compact ? ' invitation-accept-panel--compact' : ''}`}>
      <h2>{compact ? 'Team invitation' : 'Accept team invitation'}</h2>
      <dl className="invitation-accept-panel__details">
        <div>
          <dt>Workspace</dt>
          <dd>{invitation.organizationName || 'Your team workspace'}</dd>
        </div>
        <div>
          <dt>Role</dt>
          <dd>{roleLabel(invitation.membershipRole)}</dd>
        </div>
        <div>
          <dt>Invited email</dt>
          <dd>{invitation.email}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{invitation.status}</dd>
        </div>
      </dl>
      {invitation.status !== 'pending' ? (
        <p className="muted">This invitation is no longer available.</p>
      ) : null}
      {invitation.status === 'pending' && me?.email && !emailMatches ? (
        <div className="alert alert-error">
          Sign in as <strong>{invitation.email}</strong> to accept this invitation. You are signed in as{' '}
          <strong>{me.email}</strong>.
        </div>
      ) : null}
      {accepted ? (
        <div className="alert alert-success">
          Invitation accepted.{' '}
          {compact ? (
            <Link to="/">Go to dashboard</Link>
          ) : (
            'Redirecting…'
          )}
        </div>
      ) : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
      {canAccept ? (
        <div className="invitation-accept-panel__actions">
          <button type="button" className="btn btn-primary" disabled={accepting} onClick={() => void accept()}>
            {accepting ? 'Accepting…' : 'Accept invitation'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
